/**
 * Soft landscape nudge for sub-laptop portrait viewports (max-width: 1080px).
 * Visibility is CSS-driven; this module handles dismiss persistence and map resize on rotate.
 *
 * Behavior: overlay when max-width 1080px + portrait; "Continue in portrait anyway" sets
 * sessionStorage portraitNudgeDismissed=1 for the tab session. Dev test reset: ?resetPortraitNudge=1 on localhost.
 */
const STORAGE_KEY = 'portraitNudgeDismissed';
const PORTRAIT_MQ = '(max-width: 1080px) and (orientation: portrait)';
const WIDTH_MQ = '(max-width: 1080px)';
const IS_LOCAL_DEV =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

function clearDismissed() {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
        /* sessionStorage unavailable */
    }
    document.documentElement.removeAttribute('data-portrait-nudge-dismissed');
}

function isDismissed() {
    try {
        return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch (e) {
        return document.documentElement.getAttribute('data-portrait-nudge-dismissed') === 'true';
    }
}

function syncDismissedAttribute() {
    if (isDismissed()) {
        document.documentElement.setAttribute('data-portrait-nudge-dismissed', 'true');
    }
}

function dismiss() {
    try {
        sessionStorage.setItem(STORAGE_KEY, '1');
    } catch (e) {
        // sessionStorage unavailable (private mode, etc.)
    }
    document.documentElement.setAttribute('data-portrait-nudge-dismissed', 'true');
}

function invalidateMapIfNeeded() {
    const map = window.settings?.globals?.map;
    if (!map || typeof map.invalidateSize !== 'function') {
        return;
    }
    requestAnimationFrame(() => {
        map.invalidateSize();
    });
}

function addMqListener(mq, handler) {
    if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', handler);
    } else {
        mq.addListener(handler);
    }
}

function logDevState() {
    if (!IS_LOCAL_DEV) {
        return;
    }
    const portraitMq = window.matchMedia(PORTRAIT_MQ);
    const widthMq = window.matchMedia(WIDTH_MQ);
    console.info('[portrait-nudge]', {
        showNudge: portraitMq.matches && !isDismissed(),
        portraitMediaQueryMatches: portraitMq.matches,
        narrowWidthMatches: widthMq.matches,
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        dismissedThisSession: isDismissed(),
        tip:
            'Nudge is hidden after "Continue in portrait" for this tab session (reloads included). ' +
            'Reset: sessionStorage.removeItem("portraitNudgeDismissed") then reload, or open ' +
            window.location.pathname + '?resetPortraitNudge=1',
    });
}

function initPortraitNudge() {
    if (IS_LOCAL_DEV && new URLSearchParams(window.location.search).has('resetPortraitNudge')) {
        clearDismissed();
    }

    syncDismissedAttribute();
    logDevState();

    const dismissButton = document.getElementById('portrait-nudge-dismiss');
    if (dismissButton) {
        dismissButton.addEventListener('click', dismiss);
    }

    const onViewportChange = () => {
        invalidateMapIfNeeded();
    };

    addMqListener(window.matchMedia(PORTRAIT_MQ), onViewportChange);
    addMqListener(window.matchMedia(WIDTH_MQ), onViewportChange);
    window.addEventListener('orientationchange', onViewportChange);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPortraitNudge);
} else {
    initPortraitNudge();
}
